/**
 * Vuex module for AI-based recommendations.
 * 
 * Loads session/presentation/personal recommendations from the planner API
 * endpoints under /api/ai/*.
 */
import Vue from 'vue'
import { PLANNER_API_URL } from '@/config'

export const ai = {
  namespaced: true,

  state: {
    sessionRecommendations: {},      // { [sessionId]: { source, recommendations } }
    presentationRecommendations: {},  // { [presentationId]: { source, recommendations } }
    personalRecommendations: [],      // Array of personal recs based on favorites
    loading: false,
    error: null,
  },

  getters: {
    sessionRecommendations: state => state.sessionRecommendations,
    presentationRecommendations: state => state.presentationRecommendations,
    personalRecommendations: state => state.personalRecommendations,
    loading: state => state.loading,
    error: state => state.error,
  },

  mutations: {
    setSessionRecommendations(state, { id, data }) {
      Vue.set(state.sessionRecommendations, id, data)
    },
    setPresentationRecommendations(state, { id, data }) {
      Vue.set(state.presentationRecommendations, id, data)
    },
    setPersonalRecommendations(state, data) {
      state.personalRecommendations = data
    },
    setLoading(state, loading) {
      state.loading = loading
    },
    setError(state, error) {
      state.error = error
    },
  },

  actions: {
    async getSessionRecommendations({ commit, state }, sessionId) {
      // Return cached if already loaded
      if (state.sessionRecommendations[sessionId]) return

      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await Vue.axios.get(PLANNER_API_URL + '/ai/recommendations/session/' + sessionId)
        commit('setSessionRecommendations', { id: sessionId, data: response.data })
      } catch (error) {
        commit('setError', 'Could not load recommendations')
        console.warn('AI recommendations not available:', error.message)
      }
      commit('setLoading', false)
    },

    async getPresentationRecommendations({ commit, state }, presentationId) {
      if (state.presentationRecommendations[presentationId]) return

      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await Vue.axios.get(PLANNER_API_URL + '/ai/recommendations/presentation/' + presentationId)
        commit('setPresentationRecommendations', { id: presentationId, data: response.data })
      } catch (error) {
        commit('setError', 'Could not load recommendations')
        console.warn('AI recommendations not available:', error.message)
      }
      commit('setLoading', false)
    },

    async getPersonalRecommendations({ commit, rootGetters }) {
      const favorites = rootGetters.favorites || []
      if (!favorites.length) {
        commit('setPersonalRecommendations', [])
        return
      }

      const sessionIds = favorites
        .filter(f => f.reference_type_id === 1)
        .map(f => f.reference_id)
      const presentationIds = favorites
        .filter(f => f.reference_type_id === 2)
        .map(f => f.reference_id)

      if (!sessionIds.length && !presentationIds.length) {
        commit('setPersonalRecommendations', [])
        return
      }

      commit('setLoading', true)
      commit('setError', null)
      try {
        const response = await Vue.axios.post(PLANNER_API_URL + '/ai/recommendations/personal', {
          session_ids: sessionIds,
          presentation_ids: presentationIds,
        })
        commit('setPersonalRecommendations', response.data.recommendations || [])
      } catch (error) {
        commit('setError', 'Could not load personal recommendations')
        console.warn('AI personal recommendations not available:', error.message)
      }
      commit('setLoading', false)
    },
  },
}
